/* ============================================================== */
/*                                                                */
/*   CardScreenV4 · หน้า "คัดกรองผู้ป่วย" (HIS V4)                */
/*   (อาการนำ · ประวัติสุขภาพ · Vital Sign · Body Composition)     */
/*                                                                */
/*   ตามต้นแบบ PatientWorkspace.dc.html                           */
/*                                                                */
/* ============================================================== */

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  AlertTriangle,
  Calendar,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  FileText,
  Paperclip,
  Plus,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react";

import { AlertV4, ButtonV4, ChipV4, SelectV4 } from "../common/ui";
import { errorTextOf } from "../common/errorTextV4";
import { isButtonLoading } from "../common/sequence/sequenceStateV4";

import {
  asNumber,
  calculateBmi,
  getBmiCategory,
  nowHourMinuteText,
  STANDARD_VITAL_SIGN_TYPES,
  todayBeDateText,
  TRIAGE_LEVELS,
  hasSavedHealthProfile,
  BODY_COMPOSITION_OCR_WAIT_HINT,
  BODY_COMPOSITION_SAVE_HINT,
  bodyCompositionSaveGateErrorOf,
  isHealthProfileComplete,
  isVitalInputComplete,
  requiredVitalHintFor,
  vitalSeverityOf,
  SECTION_NAV,
  validateScreening,
  VITAL_REMARK_OPTIONS,
  type AllergyItem,
  type BodyCompositionRecord,
  type HealthProfileData,
  type NewVitalSignInput,
  type ScreenIds,
  type ScreenRunParams,
  type ScreenRunSequenceProp,
  type ScreenV4Slice,
  type TriageData,
  type VitalSignMeasurement,
} from "./screenModelV4";
import {
  ACTIONS as SCREEN_ACTIONS,
  CARD_NAME as SCREEN_CARD_NAME,
  SEQUENCE_NAME as SCREEN_SEQUENCE_NAME,
} from "../common/sequence/ScreenV4";
import type { MockEncounter, MockPatient } from "./mockPatientV4";

export type CardScreenV4Props = {
  patient?: MockPatient | any;
  encounter?: MockEncounter | { id?: unknown; number?: string; hn?: string; patient_id?: unknown };
  /** slice ของ ScreenV4 sequence */
  screen?: Partial<ScreenV4Slice> | null;
  ids?: ScreenIds;
  runSequence?: ScreenRunSequenceProp;
  buttonLoadCheck?: Record<string, any>;
  errorMessage?: Record<string, any>;
  onSelectMenu?: (menu: any) => void;
  onBackToQueue?: () => void;
  /** username ของผู้ใช้ที่ login (backend ใช้ยืนยงการยกเลิกการวัด) */
  currentUsername?: string;
};

const BODY_COMP_METRICS: { k: keyof BodyCompositionRecord; label: string; unit: string }[] = [
  { k: "weightKg", label: "น้ำหนัก", unit: "kg" },
  { k: "muscleKg", label: "กล้ามเนื้อโครงร่าง (SMM)", unit: "kg" },
  { k: "fatPercent", label: "% ไขมันในร่างกาย (PBF)", unit: "%" },
  { k: "bmi", label: "BMI", unit: "kg/m²" },
  { k: "visceralFat", label: "ไขมันช่องท้อง (Visceral Fat)", unit: "ระดับ" },
  { k: "bmrKcal", label: "อัตราเผาผลาญพื้นฐาน (BMR)", unit: "kcal" },
  { k: "waterPercent", label: "น้ำในร่างกาย (TBW)", unit: "%" },
  { k: "fatMassKg", label: "มวลไขมัน (Body Fat Mass)", unit: "kg" },
  { k: "ecwTbw", label: "ECW/TBW", unit: "" },
  { k: "inBodyScore", label: "InBody Score", unit: "คะแนน" },
];

/* แสดงว่า "การ์ดไหนกำลังโหลด/บันทึกอยู่" - icon หมุนท้ายชื่อการ์ด
   ขึ้นกับ buttonLoadCheck ของ sequence (BLC_STATUS.LOADING) ของ action ของการ์ดนี้เอง
   role="status" + aria-label ให้ผู้ช่วยหน้าจออ่านได้ และไม่มี text node (ไม่ทำให้ visual gate เปลี่ยน) */
const SectionSpinner: React.FC<{ running: boolean; label: string }> = ({ running, label }) => {
  if (!running) return null;
  return <span className="hv4-spin" role="status" aria-live="polite" aria-label={label} title={label} />;
};

export const CardScreenV4: React.FC<CardScreenV4Props> = (props) => {
  const {
    patient,
    encounter,
    screen,
    ids,
    runSequence,
    buttonLoadCheck,
    errorMessage,
    onSelectMenu,
    onBackToQueue,
    currentUsername,
  } = props;


  // Local state for editing triage
  const [chiefComplaint, setChiefComplaint] = useState<string>(
    screen?.triage?.chiefComplaint ?? "",
  );
  const [triageLevel, setTriageLevel] = useState<string | undefined>(
    screen?.triage?.triageLevel,
  );

  // Local state for editing health profile
  /* issue 86615 ข้อ 4: ถ้าประวัติยังกรอกไม่ครบ ให้เปิดโหมดแก้ไขเอง */
  const [profileEditMode, setProfileEditMode] = useState(
    () => !isHealthProfileComplete(props.screen?.healthProfile),
  );
  /* true เมื่อผู้ใช้กด "แก้ไขประวัติ"/"ยกเลิกการแก้ไข" เองใน mount นี้ - ห้ามค่าจาก slice มากดทับ */
  const profileModeTouchedRef = useRef(false);
  const [underlyingDiseases, setUnderlyingDiseases] = useState<string>(
    screen?.healthProfile?.underlyingDiseases ?? "ไม่มี",
  );
  const [currentMedications, setCurrentMedications] = useState<string>(
    screen?.healthProfile?.currentMedications ?? "ไม่มี",
  );
  const [surgicalHistory, setSurgicalHistory] = useState<string>(
    screen?.healthProfile?.surgicalHistory ?? "ไม่มี",
  );
  const [smoking, setSmoking] = useState<string>(
    screen?.healthProfile?.smoking ?? "ไม่สูบ",
  );
  const [alcohol, setAlcohol] = useState<string>(
    screen?.healthProfile?.alcohol ?? "ไม่ดื่ม",
  );
  const [allergies, setAllergies] = useState<AllergyItem[]>(
    screen?.healthProfile?.allergies ?? [],
  );
  const [removedAllergyIds, setRemovedAllergyIds] = useState<number[]>([]);
  const [isNkda, setIsNkda] = useState<boolean>(
    /* ห้ามเดาว่า "ไม่มีประวัติแพ้ = NKDA" - ปุ่มต้องขึ้นตามแถว NO_REACTION บน backend จริง */
    screen?.healthProfile?.nkda ?? false,
  );

  // New allergy input state
  const [newAlType, setNewAlType] = useState<"ยา" | "อาหาร" | "สารเคมี" | "อื่น ๆ">("ยา");
  const [newAlName, setNewAlName] = useState<string>("");
  const [newAlReaction, setNewAlReaction] = useState<string>("");

  // Vital sign input state ("ค่าใหม่")
  const [vOpen, setVOpen] = useState(true);
  const [newVitals, setNewVitals] = useState<NewVitalSignInput>({
    bpSys: "",
    bpDia: "",
    pulse: "",
    temp: "",
    rr: "",
    spo2: "",
    height: screen?.latestHeight ? String(screen.latestHeight) : "",
    weight: "",
    bmi: "",
    waist: "",
    painScore: "",
    remark: "แรกรับ",
    manualDate: "",
    manualTime: "",
    customDateTime: false,
    date: "",
    time: "",
  });
  const [isManualDateTime, setIsManualDateTime] = useState(false);

  useEffect(() => {
    if (screen?.latestHeight) {
      setNewVitals((prev) => (prev.height ? prev : { ...prev, height: String(screen.latestHeight) }));
    }
  }, [screen?.latestHeight]);

  // Series visibility for Vital Sign chart
  const [hiddenSeries, setHiddenSeries] = useState<Record<string, boolean>>({});
  const [vitalPage, setVitalPage] = useState<number>(0);

  // Body Composition state
  const [bcFile, setBcFile] = useState<{ name: string; size: number; kind: string; url?: string } | null>(null);
  /* File ดิบที่แนบไว้จริง (ต้องส่งเป็น multipart ตอนบันทึก เพื่อให้ backend เก็บ source_file) */
  const bcFileRef = useRef<File | null>(null);
  const [bcRawText, setBcRawText] = useState("");
  const [bcDate, setBcDate] = useState("");
  const [bcTime, setBcTime] = useState("");
  const [newBcRecord, setNewBcRecord] = useState<Partial<BodyCompositionRecord>>({});
  const [bcSelectedMetric, setBcSelectedMetric] = useState<keyof BodyCompositionRecord>("fatPercent");
  const [bcMessage, setBcMessage] = useState("");
  /* issue 86619 ข้อ 8: ประวัติแสดงครั้งละ 3 ครั้งล่าสุด เลื่อนด้วย ◀ / ▶ */
  const [bcPage, setBcPage] = useState(0);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Keep state in sync when screen slice loads
  useEffect(() => {
    if (screen?.triage) {
      setChiefComplaint(screen.triage.chiefComplaint);
      setTriageLevel(screen.triage.triageLevel);
    }
  }, [screen?.triage]);

  useEffect(() => {
    if (screen?.healthProfile) {
      setUnderlyingDiseases(screen.healthProfile.underlyingDiseases || "ไม่มี");
      setCurrentMedications(screen.healthProfile.currentMedications || "ไม่มี");
      setSurgicalHistory(screen.healthProfile.surgicalHistory || "ไม่มี");
      setSmoking(screen.healthProfile.smoking || "ไม่สูบ");
      setAlcohol(screen.healthProfile.alcohol || "ไม่ดื่ม");
      setAllergies(screen.healthProfile.allergies || []);
      setIsNkda(Boolean(screen.healthProfile.nkda));
      /* slice มาถึงทีหลัง (LoadScreen เสร็จหลัง mount / สลับ tab กลับมา):
         โหมดแก้ไขต้องตามข้อมูลจริงด้วย ไม่งั้นค้างในโหมดแก้ไขและ chip ไม่ติ๊กถูก */
      if (!profileModeTouchedRef.current) {
        setProfileEditMode(!isHealthProfileComplete(screen.healthProfile));
      }
    }
  }, [screen?.healthProfile]);

  // Auto-calculate BMI when height or weight change in newVitals
  const handleVitalChange = (field: keyof NewVitalSignInput, value: string) => {
    setNewVitals((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === "height" || field === "weight") {
        const h = field === "height" ? value : prev.height;
        const w = field === "weight" ? value : prev.weight;
        updated.bmi = calculateBmi(w, h);
      }
      return updated;
    });
  };

  // Dispatch sequence helper
  const dispatchSeq = (action: string, extraParams: Record<string, any> = {}) => {
    if (!runSequence) return;
    runSequence({
      sequence: SCREEN_SEQUENCE_NAME,
      action,
      card: SCREEN_CARD_NAME,
      encounterId: ids?.encounterId,
      patientId: ids?.patientId,
      doctorId: ids?.doctorId,
      patientHn: ids?.patientHn,
      ...extraParams,
    });
  };

  /* pre-check จาก sequence (issue 86747): Encounter นี้สร้างการคัดกรองใหม่ไม่ได้ตาม allowed_actions ของ backend */
  const triageNotice = screen?.triageNotice;

  // Handlers for triage
  const handleSaveTriage = () => {
    dispatchSeq(SCREEN_ACTIONS.SAVE_TRIAGE, {
      savedBy: currentUsername,
      triage: {
        id: screen?.triage?.id,
        chiefComplaint: chiefComplaint.trim(),
        triageLevel,
        arriveStatus: screen?.triage?.arriveStatus,
      },
    });
  };

  // Handlers for health profile
  const handleToggleNkda = () => {
    if (!isNkda) {
      /* แถวประวัติแพ้ที่เคยบันทึกไว้แล้ว (มี id) ต้องถูก CANCEL ที่ backend ด้วย
         ไม่งั้นข้อมูลจริงบน AdverseReaction ยังอยู่และจะเด้งกลับมาใหม่ทุกครั้งที่โหลดการ์ด */
      const savedIds = allergies
        .map((item) => item.id)
        .filter((id): id is number => typeof id === "number");
      if (savedIds.length > 0) setRemovedAllergyIds((ids) => [...ids, ...savedIds]);
      setIsNkda(true);
      setAllergies([]);
    } else {
      setIsNkda(false);
    }
  };

  const handleAddAllergy = () => {
    if (!newAlName.trim()) return;
    const item: AllergyItem = {
      type: newAlType,
      name: newAlName.trim(),
      react: newAlReaction.trim() || "ไม่ระบุอาการ",
    };
    setAllergies((prev) => [...prev, item]);
    setIsNkda(false);
    setNewAlName("");
    setNewAlReaction("");
  };

  const handleRemoveAllergy = (index: number) => {
    /* row ที่เคยบันทึกแล้ว (มี id ของ AdverseReaction) ต้องถูก CANCEL ที่ backend ด้วย ->
       เก็บ id ไว้ส่งไปใน SAVE_PROFILE (ProblemList/AdverseReaction ไม่อนุญาต DELETE) */
    const target = allergies[index];
    if (target && typeof target.id === "number") {
      setRemovedAllergyIds((ids) => [...ids, target.id as number]);
    }
    setAllergies((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveProfile = () => {
    const profile: HealthProfileData = {
      underlyingDiseases: underlyingDiseases.trim() || "ไม่มี",
      surgicalHistory: surgicalHistory.trim() || "ไม่มี",
      currentMedications: currentMedications.trim() || "ไม่มี",
      smoking: smoking.trim() || "ไม่สูบ",
      alcohol: alcohol.trim() || "ไม่ดื่ม",
      nkda: isNkda,
      allergies,
      recordedBy: currentUsername || screen?.healthProfile?.recordedBy || "พยาบาลคัดกรอง",
      recordedAt: new Date().toISOString(),
    };
    dispatchSeq(SCREEN_ACTIONS.SAVE_PROFILE, {
      healthProfile: profile,
      removedAllergyIds,
      savedBy: currentUsername,
    });
    setRemovedAllergyIds([]);
    profileModeTouchedRef.current = false;
    setProfileEditMode(false);
  };

  // Handlers for vital sign
  const handleSaveVitalSign = () => {
    const payload: NewVitalSignInput = {
      ...newVitals,
      customDateTime: isManualDateTime,
      date: isManualDateTime ? newVitals.manualDate || "" : "",
      time: isManualDateTime ? newVitals.manualTime || "" : "",
    };

    dispatchSeq(SCREEN_ACTIONS.SAVE_VITAL_SIGN, {
      input: payload,
      vitalSign: payload,
      savedBy: currentUsername,
    });
    /* ห้ามบอกว่าเรียบร้อยก่อนรู้ผล - sequence จะเขียน errorMessage ให้ถ้าบันทึกไม่สำเร็จ */
    setNewVitals((prev) => ({
      ...prev,
      weight: "",
      bmi: "",
      temp: "",
      bpSys: "",
      bpDia: "",
      pulse: "",
      rr: "",
      spo2: "",
      painScore: "",
    }));
  };

  const [cancelTarget, setCancelTarget] = useState<{ id?: number; label: string } | null>(null);
  const [cancelUser, setCancelUser] = useState<string>(currentUsername || "");
  const [cancelPassword, setCancelPassword] = useState("");

  const handleDeleteMeasurement = (measurementId?: number, label?: string) => {
    /* ยกเลิกการวัดต้องยืนยันตัวผู้ใช้ก่อน (backend AuthenticationForm) จึงเปิดกล่องยืนยันแทนการลบทันที */
    setCancelPassword("");
    setCancelTarget({ id: measurementId, label: label || "" });
  };

  const confirmDeleteMeasurement = () => {
    if (!cancelTarget?.id) return;
    dispatchSeq(SCREEN_ACTIONS.DELETE_MEASUREMENT, {
      measureId: cancelTarget.id,
      measurementId: cancelTarget.id,
      label: cancelTarget.label,
      username: cancelUser,
      password: cancelPassword,
    });
    setCancelTarget(null);
    setCancelPassword("");
  };

  // Handlers for Body Composition (issue 86619 + backend 86758)
  const clearBcDraft = () => {
    bcFileRef.current = null;
    setBcFile(null);
    setBcRawText("");
    setNewBcRecord({});
    setBcDate("");
    setBcTime("");
    setBcMessage("");
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const kind = file.type.startsWith("image/") ? "img" : "pdf";
    setBcFile({
      name: file.name,
      size: file.size,
      kind,
      url: kind === "img" ? URL.createObjectURL(file) : undefined,
    });
    bcFileRef.current = file;
    setBcMessage("");
    /* ให้ backend อ่านค่า (his/framework/ocr qwen) - การ์ดไม่ parse เอง เพราะกติกา extraction อยู่ฝั่ง backend */
    dispatchSeq(SCREEN_ACTIONS.OCR_BODY_COMPOSITION, { file });
  };

  const handleReparseBc = () => {
    if (!bcRawText.trim()) {
      setBcMessage("ยังไม่มีข้อความให้อ่าน กรุณาระบุข้อความที่อ่านได้จากไฟล์ (OCR)");
      return;
    }
    dispatchSeq(SCREEN_ACTIONS.OCR_BODY_COMPOSITION, { text: bcRawText });
  };

  const handleSaveBodyComp = () => {
    const record: BodyCompositionRecord = {
      dateRecorded: (bcDate || "").trim(),
      timeRecorded: (bcTime || "").trim(),
      weightKg: newBcRecord.weightKg,
      fatPercent: newBcRecord.fatPercent,
      muscleKg: newBcRecord.muscleKg,
      waterPercent: newBcRecord.waterPercent,
      bmrKcal: newBcRecord.bmrKcal,
      visceralFat: newBcRecord.visceralFat,
      bmi: newBcRecord.bmi,
      fatMassKg: newBcRecord.fatMassKg,
      ecwTbw: newBcRecord.ecwTbw,
      inBodyScore: newBcRecord.inBodyScore,
      rawOcrText: bcRawText,
      attachedFileName: bcFile?.name,
      ocrModel: screen?.bodyCompositionOcr?.ocrModel,
      ambiguities: screen?.bodyCompositionOcr?.ambiguities ?? [],
    };

    /* ข้อ 5: กดบันทึกได้เมื่อวันเวลาถูกรูปแบบ และมีน้ำหนัก > 0 */
    const gate = bodyCompositionSaveGateErrorOf(record);
    if (gate) {
      setBcMessage(gate);
      return;
    }

    dispatchSeq(SCREEN_ACTIONS.SAVE_BODY_COMPOSITION, {
      record,
      bodyComposition: record,
      file: bcFileRef.current || undefined,
    });
    clearBcDraft();
  };

  // Handlers for Queue
  const handleSendToQueue = () => {
    /* ข้อ 5: sequence เป็นผู้บันทึกทั้งหมด (SAVE_TRIAGE + SAVE_PROFILE) ก่อน OPD_SCREEN -> OPD_QUEUE_EXAM */
    dispatchSeq(SCREEN_ACTIONS.SEND_TO_QUEUE, {
      encounterStatus: screen?.encounterStatus,
      triage: {
        id: screen?.triage?.id,
        chiefComplaint: chiefComplaint.trim(),
        triageLevel,
        arriveStatus: screen?.triage?.arriveStatus,
      },
      healthProfile: currentProfile,
      removedAllergyIds,
    });
  };

  // Validation & Section Nav Status
  const currentProfile: HealthProfileData = {
    underlyingDiseases,
    surgicalHistory,
    currentMedications,
    smoking,
    alcohol,
    nkda: isNkda,
    allergies,
  };
  /* สีตามระดับความผิดปกติของค่า (issue 86617 ข้อ 3) - ค่าว่าง = "NA" ไม่ tô สี */
  const severityStyle = (code: string, value?: string): React.CSSProperties => {
    const severity = vitalSeverityOf(code, value);
    return {
      color: severity ? severity.color : "#243036",
      fontWeight: severity && severity.level > 0 ? 700 : 400,
    };
  };

  const profileComplete = isHealthProfileComplete(currentProfile);
  /* ข้อ 5: ข้อความยืนยัน取自 slice ที่ sequence เขียนหลัง POST /apis/PTM/measurement/ สำเร็จ */
  const vitalSavedText = screen?.vitalSavedAt ? `บันทึก Vital Sign แล้ว ${screen.vitalSavedAt}` : "";
  /* backend (check_allow_update) ตอบ 400 "หน่วยงาน ... ไม่สามารถบันทึก ... ได้"
     ถ้าชนิดวัดนั้นไม่ได้อยู่ใน master ของหน่วยงานของ encounter นี้ */
  const allowedVitalTypes = screen?.vitalSignTypes ?? [];
  /* master ของแต่ละหน่วยงานไม่เหมือนกัน -> ค่าบังคับของปุ่ม Save ต้องตาม master ของหน่วยงานนั้น
     (เช่น MeHealth Medical Lab มี BMI Height Weight SP DP PR - ไม่มี อุณหภูมิ/Pain score) */
  const allowedVitalCodes = allowedVitalTypes.map((t) => t.code);
  const divisionHasVitalTypes = allowedVitalTypes.length > 0;
  const vitalInputComplete =
    divisionHasVitalTypes &&
    isVitalInputComplete(
      {
        ...newVitals,
        customDateTime: isManualDateTime,
        date: isManualDateTime ? newVitals.manualDate || "" : "",
        time: isManualDateTime ? newVitals.manualTime || "" : "",
      },
      allowedVitalCodes,
    );


  /* issue 86613 ข้อ 3: หัวข้อหน้าตามด้วยสถานะการบันทึก */
  const headerSavedText = (): string => {
    if (isSent) return "สถานะ: ส่งเข้าคิวรอตรวจแล้ว";
    if (screen?.savedAt && screen?.savedBy) return `บันทึกล่าสุด ${screen.savedAt} โดย ${screen.savedBy}`;
    return "ยังไม่ได้บันทึก";
  };
  const profileAuditText = (): string => {
    const profile = screen?.healthProfile;
    if (!profile?.recordedBy) return "ยังไม่ได้บันทึกประวัติสุขภาพ";
    const stamp = profile.recordedAt ? ` · ${profile.recordedAt}` : "";
    return profile.firstSaved
      ? `บันทึกครั้งแรกโดย ${profile.recordedBy}${stamp}`
      : `บันทึกล่าสุดโดย ${profile.recordedBy}${stamp}`;
  };

  // ถ้าคนนี้มีประวัติสุขภาพอยู่แล้ว (มีโรคประจำตัว, ยาเดิม, ผ่าตัด, หรือระบุแพ้/NKDA) และไม่ได้กำลังเปิดโหมดแก้ไข ให้ติ๊กถูกสีเขียวทันที
  /* chip "2 · ประวัติสุขภาพ" ต้องติ๊กตามข้อมูลที่มีจริงใน backend (ProblemList/AdverseReaction)
     ไม่ใช่ตาม local edit mode ที่ตั้งค่าครั้งเดียวตอน mount (สลับ tab แล้วกลับมา = mount ก่อน LoadScreen จบ) */
  const hasProfile = hasSavedHealthProfile(screen?.healthProfile) || Boolean(
    !profileEditMode &&
    (
      isNkda ||
      allergies.length > 0 ||
      (underlyingDiseases && underlyingDiseases.trim() !== "" && underlyingDiseases.trim() !== "ไม่มี" && underlyingDiseases.trim() !== "ไม่ระบุ") ||
      (currentMedications && currentMedications.trim() !== "" && currentMedications.trim() !== "ไม่มี" && currentMedications.trim() !== "ไม่ระบุ") ||
      (surgicalHistory && surgicalHistory.trim() !== "" && surgicalHistory.trim() !== "ไม่มี" && surgicalHistory.trim() !== "ไม่ระบุ") ||
      (smoking && smoking !== "ไม่สูบ" && smoking !== "ไม่ระบุ") ||
      (alcohol && alcohol !== "ไม่ดื่ม" && alcohol !== "ไม่ระบุ") ||
      screen?.healthProfile?.recordedAt
    )
  );

  const measurements = screen?.vitalSignMeasurements ?? [];
  /* issue 86617 ข้อ 7: บันทึกแล้วอย่างน้อย 1 ครั้ง หรือกรอกค่าบังคับครบ -> chip ขึ้น */
  const hasVitals = measurements.length > 0 || vitalInputComplete;
  /* issue 86620 ข้อ 1-2: ข้อความสรุป + เงื่อนไขของปุ่ม "ส่งผู้ป่วยเข้าคิวรอตรวจ"
     (ประวัติสุขภาพต้องไม่อยู่ระหว่างแก้ไข, สัญญาณชีพบันทึกแล้ว >=1 หรือกรอกค่าบังคับครบ,
      Triage/Body Composition ไม่บังคับ) */
  const validation = validateScreening({
    chiefComplaint,
    healthProfile: currentProfile,
    profileEditMode,
    vitalSavedCount: measurements.length,
    vitalInputComplete,
  });
  const readyForQueue = validation.valid && hasVitals;
  const isSent = Boolean(screen?.sentToQueue);

  // Pagination for measurements table
  const PAGE_SIZE = 3;
  const maxVitalPage = Math.max(0, Math.ceil(measurements.length / PAGE_SIZE) - 1);
  const visibleMeasurements = useMemo(() => {
    const end = measurements.length - vitalPage * PAGE_SIZE;
    return measurements.slice(Math.max(0, end - PAGE_SIZE), end);
  }, [measurements, vitalPage]);

  // Loading states
  const isSavingTriage = isButtonLoading(buttonLoadCheck, {
    sequence: SCREEN_SEQUENCE_NAME,
    card: SCREEN_CARD_NAME,
    action: SCREEN_ACTIONS.SAVE_TRIAGE,
  });
  const isSavingProfile = isButtonLoading(buttonLoadCheck, {
    sequence: SCREEN_SEQUENCE_NAME,
    card: SCREEN_CARD_NAME,
    action: SCREEN_ACTIONS.SAVE_PROFILE,
  });
  const isSavingVital = isButtonLoading(buttonLoadCheck, {
    sequence: SCREEN_SEQUENCE_NAME,
    card: SCREEN_CARD_NAME,
    action: SCREEN_ACTIONS.SAVE_VITAL_SIGN,
  });
  const isSavingBc = isButtonLoading(buttonLoadCheck, {
    sequence: SCREEN_SEQUENCE_NAME,
    card: SCREEN_CARD_NAME,
    action: SCREEN_ACTIONS.SAVE_BODY_COMPOSITION,
  });
  const isSendingQueue = isButtonLoading(buttonLoadCheck, {
    sequence: SCREEN_SEQUENCE_NAME,
    card: SCREEN_CARD_NAME,
    action: SCREEN_ACTIONS.SEND_TO_QUEUE,
  });

  /* โหมด LOADING ของ action ที่การ์ดนี้ยิงอยู่ (LOAD = ดึงข้อมูลทั้งหน้า)
     ใช้ตั้ง icon หมุนหลังชื่อแต่ละหัวข้อ เพื่อให้รู้ว่ากำลังทำอะไรอยู่ */
  const isSeqActionLoading = (action: string): boolean =>
    isButtonLoading(buttonLoadCheck, {
      sequence: SCREEN_SEQUENCE_NAME,
      card: SCREEN_CARD_NAME,
      action: action as any,
    });
  const isPageLoading = isSeqActionLoading(SCREEN_ACTIONS.LOAD);
  const triageSectionLoading = isPageLoading || isSeqActionLoading(SCREEN_ACTIONS.SAVE_TRIAGE) || isSeqActionLoading(SCREEN_ACTIONS.SEND_TO_QUEUE);
  const profileSectionLoading = isPageLoading || isSeqActionLoading(SCREEN_ACTIONS.SAVE_PROFILE) || isSeqActionLoading(SCREEN_ACTIONS.SEND_TO_QUEUE);
  const vitalSectionLoading = isPageLoading || isSeqActionLoading(SCREEN_ACTIONS.SAVE_VITAL_SIGN) || isSeqActionLoading(SCREEN_ACTIONS.DELETE_MEASUREMENT);
  const bcOcrLoading = isSeqActionLoading(SCREEN_ACTIONS.OCR_BODY_COMPOSITION);
  const bcSaveLoading = isSeqActionLoading(SCREEN_ACTIONS.SAVE_BODY_COMPOSITION);
  const bcSectionLoading =
    isPageLoading || bcOcrLoading || bcSaveLoading || isSeqActionLoading(SCREEN_ACTIONS.SAVE_BODY_COMPOSITION);

  /* ผล OCR จาก backend (raw_text + values + วันเวลาจากใบ) -> เติมคอลัมน์ "ค่าใหม่" และช่องวันเวลา */
  useEffect(() => {
    const ocr = screen?.bodyCompositionOcr;
    if (!ocr) return;
    setBcRawText(ocr.rawText || "");
    setNewBcRecord((prev) => ({ ...prev, ...ocr.values }));
    if (ocr.date) setBcDate(ocr.date);
    /* ใบหลายเจ้ามีแต่วันที่เกิดการตรวจ -> เติมเวลาปัจจุบันให้เห็นชัดว่าจะบันทึกเวลาไหน */
    if (ocr.time) {
      setBcTime(ocr.time);
    } else if (ocr.date) {
      setBcTime(nowHourMinuteText());
    }
    setBcMessage("");
  }, [screen?.bodyCompositionOcr]);

  /* ประวัติ 3 ครั้งล่าสุด + ค่าที่ยังไม่บันทึก (จุดสีส้มบนกราฟ ตาม #86619 ข้อ 9) */
  const BC_PAGE_SIZE = 3;
  const bcRecords = screen?.bodyCompositionList ?? [];
  const bcMaxPage = Math.max(0, Math.ceil(bcRecords.length / BC_PAGE_SIZE) - 1);
  const bcWindow = (() => {
    const end = bcRecords.length - bcPage * BC_PAGE_SIZE;
    return bcRecords.slice(Math.max(0, end - BC_PAGE_SIZE), Math.max(0, end));
  })();
  const bcMetric = BODY_COMP_METRICS.find((f) => f.k === bcSelectedMetric) ?? BODY_COMP_METRICS[0];
  const bcGraphPoints: { date: string; time: string; value: number; unsaved: boolean }[] = [
    ...bcWindow
      .map((bc) => ({
        date: bc.dateRecorded,
        time: bc.timeRecorded,
        value: Number((bc as any)[bcMetric.k]),
        unsaved: false,
      }))
      .filter((point) => Number.isFinite(point.value)),
    ...(() => {
      const value = Number((newBcRecord as any)[bcMetric.k]);
      if (!Number.isFinite(value) || value <= 0) return [];
      return [
        {
          date: bcDate || todayBeDateText(),
          time: bcTime || nowHourMinuteText(),
          value,
          unsaved: true,
        },
      ];
    })(),
  ];
  const bcSavedMessage = screen?.bodyCompositionSavedMessage || "";
  /* gate ตาม #86619 ข้อ 5 - ใช้ทั้งการปิดปุ่มและแสดงคำเตือน */
  const bcSaveBlocked = Boolean(
    bodyCompositionSaveGateErrorOf({
      dateRecorded: bcDate,
      timeRecorded: bcTime,
      weightKg: newBcRecord.weightKg,
    }),
  );
  const bcAmbiguities = screen?.bodyCompositionOcr?.ambiguities ?? [];
  /* scale ของกราฟ: ไม่มีค่า = ไม่มีจุด (ห้ามเติมค่าปลอม) */
  const bcValueList = bcGraphPoints.map((p) => p.value);
  const bcRawMin = bcValueList.length ? Math.min(...bcValueList) : 0;
  const bcRawMax = bcValueList.length ? Math.max(...bcValueList) : 1;
  const bcSpan = bcRawMax - bcRawMin;
  const bcLo = bcSpan === 0 ? bcRawMin - 1 : bcRawMin - bcSpan * 0.18;
  const bcHi = bcSpan === 0 ? bcRawMax + 1 : bcRawMax + bcSpan * 0.18;
  const bcGraphArea = { left: 44, right: 450, top: 22, bottom: 210 };
  const bcXOf = (index: number) =>
    bcGraphPoints.length <= 1
      ? (bcGraphArea.left + bcGraphArea.right) / 2
      : bcGraphArea.left + ((bcGraphArea.right - bcGraphArea.left) * index) / (bcGraphPoints.length - 1);
  const bcYOf = (value: number) =>
    bcGraphArea.bottom -
    ((bcGraphArea.bottom - bcGraphArea.top) * (value - bcLo)) / Math.max(bcHi - bcLo, 0.0001);
  const bcNumberText = (value: number) => String(Number(value.toFixed(2)));
  const bcGridLines = [0, 1, 2, 3].map((i) => {
    const value = bcHi - ((bcHi - bcLo) * i) / 3;
    const y = bcYOf(value);
    return { y, ty: y + 4, t: bcNumberText(value) };
  });

  /* ห้ามเติมค่าปลอม (36.5/98/0) ให้จุดบนกราฟ: ไม่มีค่า = ไม่มีจุด (เหมือนแบบ 4d ที่ filter NaN) */
  const vitalPoint = (row: VitalSignMeasurement, index: number, code: string, scale = 1): string | null => {
    const raw = row.values?.[code];
    if (raw === undefined || raw === null || String(raw).trim() === "") return null;
    const value = Number(raw);
    if (!Number.isFinite(value)) return null;
    const x =
      visibleMeasurements.length <= 1 ? 234 : 70 + (index * 330) / (visibleMeasurements.length - 1);
    const y = 240 - ((value * scale) / 140) * 210;
    return `${x},${y.toFixed(1)}`;
  };
  const vitalPoints = (code: string, scale = 1): string =>
    visibleMeasurements
      .map((row, index) => vitalPoint(row, index, code, scale))
      .filter((point): point is string => point !== null)
      .join(" ");

  const err = errorMessage?.[SCREEN_CARD_NAME] || errorMessage?.[SCREEN_SEQUENCE_NAME];

  return (
    <div className="hv4-sc-main" style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Error alert if sequence failed */}
      {err && (
        <AlertV4 tone="danger">
          เกิดข้อผิดพลาด: {errorTextOf(err)}
        </AlertV4>
      )}

      {/* Header */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "4px 16px" }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>คัดกรองผู้ป่วย</h1>
        <span style={{ fontSize: 14, color: "#5B6C73" }}>
          {headerSavedText()}
        </span>
      </div>

      {/* Queue Sent Banner */}
      {isSent && (
        <div
          role="status"
          style={{
            padding: "14px 16px",
            borderRadius: 6,
            background: "#E3F5E8",
            border: "1px solid #9AD2A8",
            color: "#17592B",
            fontSize: 15,
            fontWeight: 500,
            display: "flex",
            flexWrap: "wrap",
            gap: "8px 16px",
            alignItems: "center",
          }}
        >
          <CheckCircle2 size={20} color="#1E8E3E" />
          <span>ส่งเข้าคิวรอตรวจแล้ว · Encounter #{encounter?.number || ids?.encounterId || "177"} สถานะ "รอพบแพทย์"</span>
          {onBackToQueue && (
            <button
              type="button"
              onClick={onBackToQueue}
              className="hv4-link"
              style={{ color: "#0F5F9E", marginLeft: "auto", fontWeight: 600 }}
            >
              ไปหน้าคิววันนี้ →
            </button>
          )}
        </div>
      )}

      {/* Sticky In-page Section Nav (issue 86613 ข้อ 4: chip นำทาง 3 อัน) */}
      <div className="hv4-sec-nav">
        {SECTION_NAV.map((item) => {
          const done =
            item.key === "triage"
              ? Boolean(chiefComplaint.trim())
              : item.key === "profile"
              ? hasProfile
              : hasVitals;
          return (
            <a
              key={item.id}
              href={`#${item.id}`}
              className={`hv4-sec-pill ${done ? "hv4-sec-pill-ok" : ""}`}
              style={
                done
                  ? { border: "1px solid #2E9E4F", background: "#E3F5E8", color: "#17592B" }
                  : undefined
              }
            >
              {done ? "✓ " : ""}
              {item.label}
            </a>
          );
        })}
      </div>

      {/* ============================================================== */}
      {/* SECTION 1: TRIAGE / CHIEF COMPLAINT                            */}
      {/* ============================================================== */}
      <section id="sec-triage" className="card" style={{ background: "#fff", padding: 20, borderRadius: 6, border: "1px solid #D5DEE2" }}>
        <h2 className="ch" style={{ margin: "0 0 4px", fontSize: 17, fontWeight: 700 }}>
          1 · อาการนำ
          <SectionSpinner running={triageSectionLoading} label="กำลังโหลด/บันทึกอาการนำ" />
        </h2>

        {triageNotice && (
          <div
            role="alert"
            style={{
              marginBottom: 10,
              padding: "10px 12px",
              borderRadius: 4,
              background: "#FFF7E6",
              border: "1px solid #F0C36D",
              color: "#8A3B00",
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            {triageNotice}
          </div>
        )}

        <label className="lb" htmlFor="tri_cc" style={{ display: "block", margin: "0 0 6px", fontWeight: 600, fontSize: 13, color: "#4A5A61" }}>
          อาการนำ (Chief complaint) *
        </label>
        <input
          id="tri_cc"
          className="in"
          required
          aria-required="true"
          aria-invalid={!chiefComplaint.trim()}
          placeholder="ระบุอาการสำคัญที่มารับบริการ เช่น ไข้ ไอ เจ็บคอ 2 วัน..."
          value={chiefComplaint}
          onChange={(e) => setChiefComplaint(e.target.value)}
          style={{ width: "100%", boxSizing: "border-box", minHeight: 44, borderRadius: 4, padding: "8px 12px", border: "1px solid #CBD3D7", background: "#fff", fontSize: 15, color: "#243036" }}
        />

        <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, color: "#5B6C73" }}>ระดับคัดกรอง (ไม่บังคับ)</span>
          {TRIAGE_LEVELS.map((lvl) => {
            /* ตามแบบ 4d: จอเล็ก 44x44, ปุมที่ไมเลือกพื้นขาว, ปุมที่เลือกเปลี่ยนเป็นสีของระดับ */
            const isSelected = triageLevel === lvl.level;
            return (
              <button
                key={lvl.level}
                type="button"
                onClick={() => setTriageLevel(isSelected ? undefined : lvl.level)}
                aria-pressed={isSelected}
                aria-label={`ระดับ ${lvl.level} ${lvl.name}`}
                title={lvl.desc}
                className="hv4-sc-level"
                style={{
                  minWidth: 44,
                  minHeight: 44,
                  borderRadius: 6,
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: "pointer",
                  border: `1px solid ${isSelected ? lvl.color : "#CBD3D7"}`,
                  background: isSelected ? lvl.color : "#fff",
                  color: isSelected ? lvl.ink : "#243036",
                }}
              >
                {lvl.level}
              </button>
            );
          })}
          <span style={{ fontSize: 13, color: "#5B6C73" }}>
            {triageLevel ? TRIAGE_LEVELS.find((l) => l.level === triageLevel)?.name : ""}
          </span>
        </div>
      </section>

      {/* ยกเลิกการวัดต้องยืนยันผู้ใช้/รหัสผ่าน (MeasurementCancelSerializer ใช้ AuthenticationForm) */}
      {cancelTarget && (
        <div
          className="hv4-sc-cancel"
          role="dialog"
          aria-label="ยืนยันการยกเลิกการวัดสัญญาณชีพ"
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 8,
            padding: "10px 12px",
            border: "1px solid #D93025",
            borderRadius: 6,
            background: "#FCE8E6",
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 600 }}>ยกเลิกการวัด {cancelTarget.label}</span>
          <label style={{ fontSize: 13 }}>
            ผู้ใช้
            <input
              className="hv4-sc-cancel-user"
              value={cancelUser}
              onChange={(event) => setCancelUser(event.target.value)}
              style={{ marginLeft: 6, padding: "6px 8px", border: "1px solid #B9C6CC", borderRadius: 4, minHeight: 34 }}
            />
          </label>
          <label style={{ fontSize: 13 }}>
            รหัสผ่าน
            <input
              className="hv4-sc-cancel-pass"
              type="password"
              value={cancelPassword}
              onChange={(event) => setCancelPassword(event.target.value)}
              style={{ marginLeft: 6, padding: "6px 8px", border: "1px solid #B9C6CC", borderRadius: 4, minHeight: 34 }}
            />
          </label>
          <ButtonV4 variant="danger-ghost" onClick={confirmDeleteMeasurement}>
            ยืนยันยกเลิก
          </ButtonV4>
          <ButtonV4 variant="ghost" onClick={() => setCancelTarget(null)}>
            ปิด
          </ButtonV4>
        </div>
      )}


      {/* ============================================================== */}
      {/* SECTION 2: HEALTH PROFILE                                      */}
      {/* ============================================================== */}
      <section id="sec-profile" className="card" style={{ background: "#fff", padding: 20, borderRadius: 6, border: "1px solid #D5DEE2" }}>
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-start", gap: "8px 16px" }}>
          <div>
            <h2 className="ch" style={{ margin: "0 0 4px", fontSize: 17, fontWeight: 700 }}>
              2 · ประวัติสุขภาพประจำตัว *
              <SectionSpinner running={profileSectionLoading} label="กำลังโหลด/บันทึกประวัติสุขภาพ" />
            </h2>
            <div style={{ fontSize: 12, color: "#5B6C73", marginTop: 2 }}>
              บันทึกครั้งเดียวและอัปเดตต่อเนื่อง (underlying disease, surgery, medications, allergies)
            </div>
          </div>
          <button
            type="button"
            className="bt"
            onClick={() => {
              profileModeTouchedRef.current = true;
              setProfileEditMode(!profileEditMode);
            }}
            aria-label={profileEditMode ? "ยกเลิกการแก้ไขประวัติ" : "แก้ไขประวัติ"}
            style={{
              minHeight: 44,
              padding: "0 18px",
              fontSize: 15,
              fontWeight: 600,
              gap: 8,
              display: "inline-flex",
              alignItems: "center",
              background: profileEditMode ? "#F3F6F7" : "#fff",
              color: "#243036",
              border: "1px solid #CBD3D7",
              borderRadius: 4,
              cursor: "pointer",
            }}
          >
            {profileEditMode ? "ยกเลิกการแก้ไข" : "แก้ไขประวัติ"}
          </button>
        </div>

        {/* Read Mode */}
        {!profileEditMode ? (
          <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
            {/* Allergies banner */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "4px 16px",
                padding: "10px 12px",
                borderRadius: 6,
                fontSize: 15,
                background: isNkda ? "#E3F5E8" : allergies.length > 0 ? "#FBE0DD" : "#EEF3F4",
                color: isNkda ? "#17592B" : allergies.length > 0 ? "#5A120B" : "#243036",
              }}
            >
              <b style={{ flex: "0 0 130px" }}>ประวัติแพ้:</b>
              <span style={{ flex: "1 1 200px" }}>
                {isNkda ? (
                  "ปฏิเสธการแพ้ทุกประเภท (NKDA)"
                ) : allergies.length > 0 ? (
                  allergies.map((a, i) => (
                    <span key={i} style={{ marginRight: 10, display: "inline-block" }}>
                      <b>{a.type}: {a.name}</b> ({a.react})
                    </span>
                  ))
                ) : (
                  "ยังไม่มีข้อมูล"
                )}
              </span>
            </div>

            {/* Profile fields */}
            {[
              { k: "โรคประจำตัว", v: underlyingDiseases },
              { k: "ยาที่ใช้ประจำ", v: currentMedications },
              { k: "ประวัติผ่าตัด", v: surgicalHistory },
              { k: "สูบบุหรี่", v: smoking },
              { k: "ดื่มสุรา", v: alcohol },
            ].map((row, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "4px 16px",
                  padding: "10px 12px",
                  borderRadius: 6,
                  fontSize: 15,
                  background: "#EEF3F4",
                }}
              >
                <b style={{ flex: "0 0 130px" }}>{row.k}:</b>
                <span style={{ flex: "1 1 200px" }}>{row.v || "ไม่มี"}</span>
              </div>
            ))}

            <div style={{ marginTop: 10, fontSize: 13, color: "#5B6C73" }}>
              {profileAuditText()}
            </div>
          </div>
        ) : (
          /* Edit Mode */
          <div style={{ marginTop: 14 }}>
            <div className="g" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
              <div>
                <label className="lb" htmlFor="prof_chronic" style={{ display: "block", margin: "0 0 6px", fontWeight: 600, fontSize: 13, color: "#4A5A61" }}>
                  โรคประจำตัว *
                </label>
                <input
                  id="prof_chronic"
                  className="in"
                  value={underlyingDiseases}
                  placeholder="เช่น เบาหวาน, ความดันสูง (ถ้าไม่มีพิมพ์ ไม่มี)"
                  onChange={(e) => setUnderlyingDiseases(e.target.value)}
                  style={{ width: "100%", boxSizing: "border-box", minHeight: 44, padding: "8px 12px", borderRadius: 4, border: "1px solid #CBD3D7", background: "#fff", fontSize: 15, color: "#243036" }}
                />
              </div>

              <div>
                <label className="lb" htmlFor="prof_meds" style={{ display: "block", margin: "0 0 6px", fontWeight: 600, fontSize: 13, color: "#4A5A61" }}>
                  ยาที่ใช้ประจำ *
                </label>
                <input
                  id="prof_meds"
                  className="in"
                  value={currentMedications}
                  placeholder="เช่น Metformin 500mg (ถ้าไม่มีพิมพ์ ไม่มี)"
                  onChange={(e) => setCurrentMedications(e.target.value)}
                  style={{ width: "100%", boxSizing: "border-box", minHeight: 44, padding: "8px 12px", borderRadius: 4, border: "1px solid #CBD3D7", background: "#fff", fontSize: 15, color: "#243036" }}
                />
              </div>

              <div>
                <label className="lb" htmlFor="prof_surg" style={{ display: "block", margin: "0 0 6px", fontWeight: 600, fontSize: 13, color: "#4A5A61" }}>
                  ประวัติผ่าตัด *
                </label>
                <input
                  id="prof_surg"
                  className="in"
                  value={surgicalHistory}
                  placeholder="เช่น ผ่าตัดไส้ติ่ง 2560 (ถ้าไม่มีพิมพ์ ไม่มี)"
                  onChange={(e) => setSurgicalHistory(e.target.value)}
                  style={{ width: "100%", boxSizing: "border-box", minHeight: 44, padding: "8px 12px", borderRadius: 4, border: "1px solid #CBD3D7", background: "#fff", fontSize: 15, color: "#243036" }}
                />
              </div>

              <div>
                <label className="lb" htmlFor="prof_smoke" style={{ display: "block", margin: "0 0 6px", fontWeight: 600, fontSize: 13, color: "#4A5A61" }}>
                  สูบบุหรี่ *
                </label>
                <input
                  id="prof_smoke"
                  className="in"
                  value={smoking}
                  placeholder="ไม่สูบ / สูบ 5 มวนต่อวัน / เคยสูบเลิกแล้ว"
                  onChange={(e) => setSmoking(e.target.value)}
                  style={{ width: "100%", boxSizing: "border-box", minHeight: 44, padding: "8px 12px", borderRadius: 4, border: "1px solid #CBD3D7", background: "#fff", fontSize: 15, color: "#243036" }}
                />
              </div>

              <div>
                <label className="lb" htmlFor="prof_alc" style={{ display: "block", margin: "0 0 6px", fontWeight: 600, fontSize: 13, color: "#4A5A61" }}>
                  ดื่มสุรา *
                </label>
                <input
                  id="prof_alc"
                  className="in"
                  value={alcohol}
                  placeholder="ไม่ดื่ม / ดื่มสังสรรค์ / ดื่มประจำ"
                  onChange={(e) => setAlcohol(e.target.value)}
                  style={{ width: "100%", boxSizing: "border-box", minHeight: 44, padding: "8px 12px", borderRadius: 4, border: "1px solid #CBD3D7", background: "#fff", fontSize: 15, color: "#243036" }}
                />
              </div>
            </div>

            {/* Allergies sub-section */}
            <div style={{ marginTop: 18, borderTop: "1px solid #E3E9EC", paddingTop: 14 }}>
              <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: "8px 16px" }}>
                <b style={{ fontSize: 15 }}>ประวัติแพ้ยา / แพ้อาหาร *</b>
                <button
                  type="button"
                  onClick={handleToggleNkda}
                  aria-pressed={isNkda}
                  style={{
                    minHeight: 40,
                    padding: "0 16px",
                    borderRadius: 20,
                    fontSize: 14,
                    fontWeight: 600,
                    border: `1px solid ${isNkda ? "#0A5148" : "#CBD3D7"}`,
                    background: isNkda ? "#D6EFEA" : "#fff",
                    color: "#243036",
                    cursor: "pointer",
                  }}
                >
                  {isNkda ? "✓ ปฏิเสธการแพ้ทุกประเภท (NKDA)" : "ปฏิเสธการแพ้ทุกประเภท"}
                </button>
              </div>

              {/* Add allergy form */}
              <div
                style={{
                  marginTop: 12,
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
                  gap: 12,
                  alignItems: "end",
                }}
              >
                <div>
                  <label className="lb" htmlFor="al_type" style={{ display: "block", marginBottom: 4, fontSize: 13 }}>ประเภท</label>
                  <select
                    id="al_type"
                    className="in"
                    value={newAlType}
                    onChange={(e) => setNewAlType(e.target.value as any)}
                    style={{ width: "100%", minHeight: 40, borderRadius: 4, border: "1px solid #CBD3D7" }}
                  >
                    <option value="ยา">ยา</option>
                    <option value="อาหาร">อาหาร</option>
                    <option value="สารเคมี">สารเคมี</option>
                    <option value="อื่น ๆ">อื่น ๆ</option>
                  </select>
                </div>

                <div>
                  <label className="lb" htmlFor="al_name" style={{ display: "block", marginBottom: 4, fontSize: 13 }}>ชื่อสิ่งที่แพ้</label>
                  <input
                    id="al_name"
                    className="in"
                    value={newAlName}
                    placeholder="เช่น Penicillin, กุ้ง"
                    onChange={(e) => setNewAlName(e.target.value)}
                    style={{ width: "100%", minHeight: 40, boxSizing: "border-box", padding: "6px 10px", borderRadius: 4, border: "1px solid #CBD3D7" }}
                  />
                </div>

                <div>
                  <label className="lb" htmlFor="al_react" style={{ display: "block", marginBottom: 4, fontSize: 13 }}>อาการที่พบ</label>
                  <input
                    id="al_react"
                    className="in"
                    value={newAlReaction}
                    placeholder="เช่น ผื่นคัน, แน่นหน้าอก"
                    onChange={(e) => setNewAlReaction(e.target.value)}
                    style={{ width: "100%", minHeight: 40, boxSizing: "border-box", padding: "6px 10px", borderRadius: 4, border: "1px solid #CBD3D7" }}
                  />
                </div>

                <button
                  type="button"
                  onClick={handleAddAllergy}
                  disabled={!newAlName.trim()}
                  style={{
                    minHeight: 40,
                    padding: "0 16px",
                    borderRadius: 4,
                    border: 0,
                    background: newAlName.trim() ? "#1B7FCC" : "#8C9BA0",
                    color: "#fff",
                    fontWeight: 600,
                    cursor: newAlName.trim() ? "pointer" : "not-allowed",
                  }}
                >
                  + เพิ่มรายการแพ้
                </button>
              </div>

              {/* Added allergies list */}
              {allergies.length > 0 && (
                <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                  {allergies.map((al, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                        padding: "8px 12px",
                        borderRadius: 6,
                        background: "#FBE0DD",
                        color: "#5A120B",
                        fontSize: 14,
                      }}
                    >
                      <span>
                        <b>{al.type}: {al.name}</b> · {al.react}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveAllergy(idx)}
                        aria-label={`ลบรายการแพ้ ${al.name}`}
                        style={{
                          border: 0,
                          borderRadius: 4,
                          background: "#fff",
                          color: "#8A1C12",
                          fontWeight: 700,
                          padding: "4px 8px",
                          cursor: "pointer",
                        }}
                      >
                        ลบ
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Save profile button (issue 86615 ข้อ 7: ยังไม่ครบ -> ปุมสีเทา + ขอความบอก) */}
            <div style={{ marginTop: 16, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
              <button
                type="button"
                className="bt"
                onClick={handleSaveProfile}
                disabled={isSavingProfile || !profileComplete}
                aria-disabled={!profileComplete}
                style={{
                  background: profileComplete ? "#1E8E3E" : "#8C9BA0",
                  color: "#fff",
                  borderRadius: 4,
                  padding: "8px 24px",
                  fontWeight: 600,
                  border: 0,
                  cursor: profileComplete ? "pointer" : "not-allowed",
                }}
              >
                {isSavingProfile ? "กำลังบันทึก..." : "บันทึกประวัติสุขภาพ"}
              </button>
              {!profileComplete && (
                <span style={{ fontSize: 13, color: "#8A3B00" }}>
                  กรอกให้ครบทุกข้อ (ไม่มีให้พิมพ์ “ไม่มี”) แล้วจึงบันทึกได้
                </span>
              )}
            </div>
          </div>
        )}
      </section>

      {/* ============================================================== */}
      {/* SECTION 3: VITAL SIGN & NEUROLOGICAL SIGN                      */}
      {/* ============================================================== */}
      <section id="sec-vital" className="card" style={{ background: "#fff", padding: "12px 8px 16px", borderRadius: 6, border: "1px solid #D5DEE2" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingBottom: 8,
            borderBottom: "1px solid #DDE3E6",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#1F2A30" }}>
              3 · สัญญาณชีพ (Vital Sign / Neurological Sign) *
              <SectionSpinner running={vitalSectionLoading} label="กำลังโหลด/บันทึกสัญญาณชีพ" />
            </h2>
          </div>
          <button
            type="button"
            onClick={() => setVOpen(!vOpen)}
            aria-expanded={vOpen}
            aria-label="ย่อ/ขยาย Vital Sign"
            style={{
              border: 0,
              background: "transparent",
              color: "#1F6FB5",
              fontSize: 16,
              cursor: "pointer",
              padding: "4px 8px",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            {vOpen ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          </button>
        </div>

        {vOpen && (
          <div style={{ marginTop: 22 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start" }}>
              {/* Left Column: Interactive SVG Trend Graph */}
              <div style={{ flex: "1 1 360px", minWidth: 300 }}>
                {/* Series selector chips */}
                <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "6px 8px", marginBottom: 8 }}>
                  {[
                    { key: "temp", label: "อุณหภูมิ (°C)", color: "#808080" },
                    { key: "pulse", label: "ชีพจร (bpm)", color: "#00C800" },
                    { key: "spo2", label: "O₂ Sat (%)", color: "#00E5E5" },
                    { key: "rr", label: "หายใจ (bpm)", color: "#FFD91A" },
                    { key: "painScore", label: "Pain", color: "#000000" },
                    { key: "bp", label: "ความดัน (mmHg)", color: "#FF0000" },
                  ].map((s) => {
                    const isHidden = hiddenSeries[s.key];
                    return (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => setHiddenSeries((prev) => ({ ...prev, [s.key]: !prev[s.key] }))}
                        aria-pressed={!isHidden}
                        style={{
                          minHeight: 30,
                          padding: "0 10px",
                          borderRadius: 15,
                          border: `1px solid ${isHidden ? "#E3E9EC" : "#CBD3D7"}`,
                          background: isHidden ? "#F5F7F8" : "#fff",
                          fontSize: 12,
                          color: isHidden ? "#9AA9AE" : "#243036",
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                        }}
                      >
                        <span
                          style={{
                            display: "inline-block",
                            width: 10,
                            height: 10,
                            borderRadius: 5,
                            marginRight: 6,
                            background: s.color,
                            opacity: isHidden ? 0.3 : 1,
                          }}
                        />
                        {s.label}
                      </button>
                    );
                  })}
                </div>

                {/* SVG Trend Graph */}
                <div style={{ border: "1px solid #E3E9EC", borderRadius: 4, overflow: "hidden", background: "#fff" }}>
                  <svg viewBox="0 0 460 280" width="100%" role="img" aria-label="กราฟแนวโน้ม Vital Sign">
                    {/* Grid lines and Y axis numbers */}
                    {[0, 20, 40, 60, 80, 100, 120, 140].map((yVal) => {
                      const yPos = 240 - (yVal / 140) * 210;
                      return (
                        <g key={yVal}>
                          <line x1={44} x2={424} y1={yPos} y2={yPos} stroke="#EFEFEF" strokeWidth={1} />
                          <text x={38} y={yPos + 4} textAnchor="end" fontSize={11} fill="#777">
                            {yVal}
                          </text>
                        </g>
                      );
                    })}
                    {/* แกนขวา 0-10 ของ Pain Score */}
                    {[0, 2, 4, 6, 8, 10].map((pVal) => {
                      const yPos = 240 - (pVal / 10) * 210;
                      return (
                        <text key={`pain-${pVal}`} x={430} y={yPos + 4} fontSize={11} fill="#666">
                          {pVal}
                        </text>
                      );
                    })}

                    {/* Graph box borders */}
                    <line x1={44} x2={44} y1={25} y2={240} stroke="#BBB" strokeWidth={1} />
                    <line x1={424} x2={424} y1={25} y2={240} stroke="#BBB" strokeWidth={1} />
                    <line x1={44} x2={424} y1={240} y2={240} stroke="#BBB" strokeWidth={1} />

                    {/* Plot data points */}
                    {visibleMeasurements.length > 0 && (
                      <>
                        {/* Pulse line */}
                        {!hiddenSeries.pulse && (
                          <polyline
                            fill="none"
                            stroke="#00C800"
                            strokeWidth={2}
                            points={vitalPoints("PULSE", 1)}
                          />
                        )}

                        {/* Temp line */}
                        {!hiddenSeries.temp && (
                          <polyline
                            fill="none"
                            stroke="#808080"
                            strokeWidth={2}
                            points={vitalPoints("TEMP", 2.5)}
                          />
                        )}

                        {/* SpO2 line */}
                        {!hiddenSeries.spo2 && (
                          <polyline
                            fill="none"
                            stroke="#00E5E5"
                            strokeWidth={2}
                            points={vitalPoints("SPO2", 1)}
                          />
                        )}

                        {/* Respiratory rate line */}
                        {!hiddenSeries.rr && (
                          <polyline
                            fill="none"
                            stroke="#FFD91A"
                            strokeWidth={2}
                            points={vitalPoints("RR", 1)}
                          />
                        )}

                        {/* Pain line: ใช้แกนขวา 0-10 (issue 86618 ข้อ 7) */}
                        {!hiddenSeries.painScore && (
                          <polyline
                            fill="none"
                            stroke="#000000"
                            strokeWidth={2}
                            strokeDasharray="5 3"
                            points={vitalPoints("PAIN_SCORE", 14)}
                          />
                        )}

                        {/* BP bars */}
                        {!hiddenSeries.bp &&
                          visibleMeasurements.map((m, idx) => {
                            const sys = parseFloat(m.values?.["BP_SYS"] || "0");
                            const dia = parseFloat(m.values?.["BP_DIA"] || "0");
                            if (!sys || !dia) return null;
                            const x = visibleMeasurements.length <= 1 ? 234 : 70 + (idx * 330) / (visibleMeasurements.length - 1);
                            const y1 = 240 - (sys / 140) * 210;
                            const y2 = 240 - (dia / 140) * 210;
                            return (
                              <line
                                key={idx}
                                x1={x}
                                x2={x}
                                y1={y1}
                                y2={y2}
                                stroke="#FF0000"
                                strokeWidth={5}
                              />
                            );
                          })}

                        {visibleMeasurements.length === 0 && (
                          <text x={234} y={140} textAnchor="middle" fill="#9AA9AE" fontSize={13}>
                            ยังไม่มีข้อมูลสัญญาณชีพในระบบ
                          </text>
                        )}
                        {/* X-axis time labels */}
                        {visibleMeasurements.map((m, idx) => {
                          const x = visibleMeasurements.length <= 1 ? 234 : 70 + (idx * 330) / (visibleMeasurements.length - 1);
                          return (
                            <text
                              key={idx}
                              x={x}
                              y={262}
                              textAnchor="middle"
                              fontSize={11}
                              fill="#555"
                            >
                              {m.timeRecorded || m.dateRecorded}
                            </text>
                          );
                        })}
                      </>
                    )}
                  </svg>
                </div>

                {/* ประวัติการยกเลิกการวัด (audit log - issue 86618 ข้อ 6) */}
                {(screen?.vitalAudit || []).length > 0 && (
                  <div
                    style={{
                      marginTop: 8,
                      padding: "8px 10px",
                      borderRadius: 4,
                      background: "#F6F8F9",
                      border: "1px solid #E3E9EC",
                      fontSize: 12,
                      color: "#5B6C73",
                    }}
                  >
                    <div style={{ fontWeight: 700, color: "#243036", marginBottom: 4 }}>
                      ประวัติการยกเลิกการวัด
                    </div>
                    <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                      {(screen?.vitalAudit || []).map((a) => (
                        <li key={`${a.measurementId}-${a.at}`}>
                          #{a.measurementId} ({a.label}) · ยกเลิกโดย {a.user} เมื่อ {a.at}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Pagination for measurements */}
                <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 6 }}>
                  <button
                    type="button"
                    onClick={() => setVitalPage((p) => Math.min(maxVitalPage, p + 1))}
                    disabled={vitalPage >= maxVitalPage}
                    aria-label="ย้อนไปค่าก่อนหน้า"
                    style={{
                      minWidth: 44,
                      minHeight: 34,
                      border: 0,
                      borderRadius: 4,
                      background: "#7B7B7B",
                      color: "#fff",
                      cursor: vitalPage >= maxVitalPage ? "not-allowed" : "pointer",
                      opacity: vitalPage >= maxVitalPage ? 0.4 : 1,
                    }}
                  >
                    ◀
                  </button>
                  <button
                    type="button"
                    onClick={() => setVitalPage((p) => Math.max(0, p - 1))}
                    disabled={vitalPage <= 0}
                    aria-label="ไปค่าล่าสุด"
                    style={{
                      minWidth: 44,
                      minHeight: 34,
                      border: 0,
                      borderRadius: 4,
                      background: "#7B7B7B",
                      color: "#fff",
                      cursor: vitalPage <= 0 ? "not-allowed" : "pointer",
                      opacity: vitalPage <= 0 ? 0.4 : 1,
                    }}
                  >
                    ▶
                  </button>
                </div>
              </div>

              {/* Right Column: Comparative History Table + "ค่าใหม่" */}
              <div style={{ flex: "1.3 1 400px", minWidth: 320, overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 14,
                    textAlign: "center",
                    border: "1px solid #E3E9EC",
                  }}
                >
                  <thead>
                    <tr style={{ background: "#EAF4F6", color: "#243036", fontWeight: 600 }}>
                      <th style={{ padding: "10px 8px", textAlign: "left", width: 140 }}>Vital Sign</th>
                      {visibleMeasurements.map((m, idx) => (
                        <th key={idx} style={{ padding: "8px 4px", borderLeft: "2px solid #fff", minWidth: 85 }}>
                          {/* badge Remark: backend (Measurement/VitalSignValue) ยังไม่มี field remark
                              จึงแสดงเมื่อมีค่าจริงเท่านั้น ไม่ default ว่า "แรกรับ" (กันข้อมูลหลอก) */}
                          {m.remark && (
                            <span
                              style={{
                                display: "inline-block",
                                padding: "1px 6px",
                                borderRadius: 8,
                                fontSize: 11,
                                background: m.remark.includes("ก่อน")
                                  ? "#FFF1C9"
                                  : m.remark.includes("หลัง")
                                  ? "#E3F5E8"
                                  : "#DDF1F6",
                                color: "#243036",
                                marginBottom: 2,
                              }}
                            >
                              {m.remark}
                            </span>
                          )}
                          <div style={{ fontSize: 11, fontWeight: 400, lineHeight: 1.25 }}>
                            {m.dateRecorded}
                            {m.encounterId && m.encounterId !== ids?.encounterId ? (
                              /* use_patient=true -> ประวัติย้อนหลังรวมทุก Encounter ของ HN (ข้อ 8) */
                              <span style={{ color: "#5B6C73" }}> · Enc #{m.encounterId}</span>
                            ) : null}
                          </div>
                          <div style={{ fontSize: 11, lineHeight: 1.25 }}>
                            <b>{m.timeRecorded}</b>
                            <button
                              type="button"
                              onClick={() => handleDeleteMeasurement(m.id, `${m.dateRecorded} ${m.timeRecorded}`)}
                              aria-label={`ลบรายการ ${m.timeRecorded || m.dateRecorded}`}
                              title="ลบรายการนี้"
                              style={{
                                border: 0,
                                background: "transparent",
                                color: "#9AA9AE",
                                cursor: "pointer",
                                padding: "0 2px",
                                fontSize: 12,
                              }}
                            >
                              ✖
                            </button>
                          </div>
                        </th>
                      ))}
                      <th style={{ padding: "10px 8px", borderLeft: "2px solid #fff", background: "#D7EFF4", minWidth: 100 }}>
                        ค่าใหม่
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* BMI row */}
                    <tr style={{ borderTop: "1px solid #E3E9EC" }}>
                      <td style={{ padding: "10px 8px", textAlign: "left" }}>BMI (kg/m²)</td>
                      {visibleMeasurements.map((m, idx) => {
                        const bmiVal = m.values?.["BMI"] || "NA";
                        const bmiNum = parseFloat(bmiVal);
                        const cat = !isNaN(bmiNum) ? getBmiCategory(bmiNum) : null;
                        return (
                          <td
                            key={idx}
                            style={{
                              padding: "10px 8px",
                              borderLeft: "1px solid #E3E9EC",
                              color: cat ? cat.color : "#243036",
                              fontWeight: cat && cat.category !== "ปกติ" ? 700 : 400,
                            }}
                          >
                            {bmiVal}
                          </td>
                        );
                      })}
                      <td style={{ padding: "4px 6px", borderLeft: "1px solid #E3E9EC" }}>
                        <input
                          id="vit_bmi"
                          readOnly
                          placeholder="คำนวณอัตโนมัติ"
                          value={newVitals.bmi}
                          style={{
                            width: "100%",
                            boxSizing: "border-box",
                            padding: "4px 8px",
                            textAlign: "center",
                            border: "1px solid #CBD3D7",
                            borderRadius: 4,
                            background: "#F5F7F8",
                            fontSize: 14,
                          }}
                        />
                      </td>
                    </tr>

                    {/* Weight */}
                    <tr style={{ borderTop: "1px solid #E3E9EC" }}>
                      <td style={{ padding: "10px 8px", textAlign: "left" }}>น้ำหนัก (kg)</td>
                      {visibleMeasurements.map((m, idx) => (
                        <td key={idx} style={{ padding: "8px 4px", borderLeft: "1px solid #E3E9EC" }}>
                          <span style={severityStyle("WEIGHT", m.values?.["WEIGHT"])}>{m.values?.["WEIGHT"] ?? "NA"}</span>
                        </td>
                      ))}
                      <td style={{ padding: "4px 6px", borderLeft: "1px solid #E3E9EC" }}>
                        <input
                          id="vit_w"
                          type="number"
                          step="0.1"
                          placeholder="NA"
                          value={newVitals.weight}
                          onChange={(e) => handleVitalChange("weight", e.target.value)}
                          style={{
                            width: "100%",
                            boxSizing: "border-box",
                            padding: "4px 8px",
                            textAlign: "center",
                            border: "1px solid #CBD3D7",
                            borderRadius: 4,
                            fontSize: 14,
                          }}
                        />
                      </td>
                    </tr>

                    {/* Height */}
                    <tr style={{ borderTop: "1px solid #E3E9EC" }}>
                      <td style={{ padding: "10px 8px", textAlign: "left" }}>ส่วนสูง (cm)</td>
                      {visibleMeasurements.map((m, idx) => (
                        <td key={idx} style={{ padding: "8px 4px", borderLeft: "1px solid #E3E9EC" }}>
                          <span style={severityStyle("HEIGHT", m.values?.["HEIGHT"])}>{m.values?.["HEIGHT"] ?? "NA"}</span>
                        </td>
                      ))}
                      <td style={{ padding: "4px 6px", borderLeft: "1px solid #E3E9EC" }}>
                        <input
                          id="vit_h"
                          type="number"
                          placeholder="NA"
                          value={newVitals.height}
                          onChange={(e) => handleVitalChange("height", e.target.value)}
                          style={{
                            width: "100%",
                            boxSizing: "border-box",
                            padding: "4px 8px",
                            textAlign: "center",
                            border: "1px solid #CBD3D7",
                            borderRadius: 4,
                            fontSize: 14,
                          }}
                        />
                      </td>
                    </tr>

                    {/* Body Temperature */}
                    <tr style={{ borderTop: "1px solid #E3E9EC" }}>
                      <td style={{ padding: "10px 8px", textAlign: "left" }}>Body temp (°C)</td>
                      {visibleMeasurements.map((m, idx) => {
                        const tempVal = parseFloat(m.values?.["TEMP"] || "0");
                        const isFever = tempVal >= 37.5;
                        return (
                          <td
                            key={idx}
                            style={{
                              padding: "10px 8px",
                              borderLeft: "1px solid #E3E9EC",
                              color: isFever ? "#D93025" : "#243036",
                              fontWeight: isFever ? 700 : 400,
                            }}
                          >
                            <span style={severityStyle("TEMP", m.values?.["TEMP"])}>{m.values?.["TEMP"] ?? "NA"}</span>
                          </td>
                        );
                      })}
                      <td style={{ padding: "4px 6px", borderLeft: "1px solid #E3E9EC" }}>
                        <input
                          id="vit_temp"
                          type="number"
                          step="0.1"
                          placeholder="NA"
                          value={newVitals.temp}
                          onChange={(e) => handleVitalChange("temp", e.target.value)}
                          style={{
                              color: severityStyle("TEMP", newVitals.temp).color,
                            width: "100%",
                            boxSizing: "border-box",
                            padding: "4px 8px",
                            textAlign: "center",
                            border: "1px solid #CBD3D7",
                            borderRadius: 4,
                            fontSize: 14,
                          }}
                        />
                      </td>
                    </tr>

                    {/* Systolic */}
                    <tr style={{ borderTop: "1px solid #E3E9EC" }}>
                      <td style={{ padding: "10px 8px", textAlign: "left" }}>Systolic (mmHg)</td>
                      {visibleMeasurements.map((m, idx) => {
                        const sys = parseFloat(m.values?.["BP_SYS"] || "0");
                        const isHigh = sys >= 140;
                        return (
                          <td
                            key={idx}
                            style={{
                              padding: "10px 8px",
                              borderLeft: "1px solid #E3E9EC",
                              color: isHigh ? "#D93025" : "#243036",
                              fontWeight: isHigh ? 700 : 400,
                            }}
                          >
                            <span style={severityStyle("BP_SYS", m.values?.["BP_SYS"])}>{m.values?.["BP_SYS"] ?? "NA"}</span>
                          </td>
                        );
                      })}
                      <td style={{ padding: "4px 6px", borderLeft: "1px solid #E3E9EC" }}>
                        <input
                          id="vit_sys"
                          type="number"
                          placeholder="NA"
                          value={newVitals.bpSys}
                          onChange={(e) => handleVitalChange("bpSys", e.target.value)}
                          style={{
                              color: severityStyle("BP_SYS", newVitals.bpSys).color,
                            width: "100%",
                            boxSizing: "border-box",
                            padding: "4px 8px",
                            textAlign: "center",
                            border: "1px solid #CBD3D7",
                            borderRadius: 4,
                            fontSize: 14,
                          }}
                        />
                      </td>
                    </tr>

                    {/* Diastolic */}
                    <tr style={{ borderTop: "1px solid #E3E9EC" }}>
                      <td style={{ padding: "10px 8px", textAlign: "left" }}>Diastolic (mmHg)</td>
                      {visibleMeasurements.map((m, idx) => {
                        const dia = parseFloat(m.values?.["BP_DIA"] || "0");
                        const isHigh = dia >= 90;
                        return (
                          <td
                            key={idx}
                            style={{
                              padding: "10px 8px",
                              borderLeft: "1px solid #E3E9EC",
                              color: isHigh ? "#D93025" : "#243036",
                              fontWeight: isHigh ? 700 : 400,
                            }}
                          >
                            <span style={severityStyle("BP_DIA", m.values?.["BP_DIA"])}>{m.values?.["BP_DIA"] ?? "NA"}</span>
                          </td>
                        );
                      })}
                      <td style={{ padding: "4px 6px", borderLeft: "1px solid #E3E9EC" }}>
                        <input
                          id="vit_dia"
                          type="number"
                          placeholder="NA"
                          value={newVitals.bpDia}
                          onChange={(e) => handleVitalChange("bpDia", e.target.value)}
                          style={{
                              color: severityStyle("BP_DIA", newVitals.bpDia).color,
                            width: "100%",
                            boxSizing: "border-box",
                            padding: "4px 8px",
                            textAlign: "center",
                            border: "1px solid #CBD3D7",
                            borderRadius: 4,
                            fontSize: 14,
                          }}
                        />
                      </td>
                    </tr>

                    {/* Pulse */}
                    <tr style={{ borderTop: "1px solid #E3E9EC" }}>
                      <td style={{ padding: "10px 8px", textAlign: "left" }}>Pulse (bpm)</td>
                      {visibleMeasurements.map((m, idx) => (
                        <td key={idx} style={{ padding: "8px 4px", borderLeft: "1px solid #E3E9EC" }}>
                          <span style={severityStyle("PULSE", m.values?.["PULSE"])}>{m.values?.["PULSE"] ?? "NA"}</span>
                        </td>
                      ))}
                      <td style={{ padding: "4px 6px", borderLeft: "1px solid #E3E9EC" }}>
                        <input
                          id="vit_pulse"
                          type="number"
                          placeholder="NA"
                          value={newVitals.pulse}
                          onChange={(e) => handleVitalChange("pulse", e.target.value)}
                          style={{
                              color: severityStyle("PULSE", newVitals.pulse).color,
                            width: "100%",
                            boxSizing: "border-box",
                            padding: "4px 8px",
                            textAlign: "center",
                            border: "1px solid #CBD3D7",
                            borderRadius: 4,
                            fontSize: 14,
                          }}
                        />
                      </td>
                    </tr>

                    {/* Pain Score */}
                    <tr style={{ borderTop: "1px solid #E3E9EC" }}>
                      <td style={{ padding: "10px 8px", textAlign: "left" }}>Pain Score (0-10)</td>
                      {visibleMeasurements.map((m, idx) => (
                        <td key={idx} style={{ padding: "8px 4px", borderLeft: "1px solid #E3E9EC" }}>
                          <span style={severityStyle("PAIN_SCORE", m.values?.["PAIN_SCORE"])}>{m.values?.["PAIN_SCORE"] ?? "NA"}</span>
                        </td>
                      ))}
                      <td style={{ padding: "4px 6px", borderLeft: "1px solid #E3E9EC" }}>
                        <input
                          id="vit_pain"
                          type="number"
                          min={0}
                          max={10}
                          placeholder="NA"
                          value={newVitals.painScore}
                          onChange={(e) => handleVitalChange("painScore", e.target.value)}
                          style={{
                              color: severityStyle("PAIN_SCORE", newVitals.painScore).color,
                            width: "100%",
                            boxSizing: "border-box",
                            padding: "4px 8px",
                            textAlign: "center",
                            border: "1px solid #CBD3D7",
                            borderRadius: 4,
                            fontSize: 14,
                          }}
                        />
                      </td>
                    </tr>

                    {/* SpO2 */}
                    <tr style={{ borderTop: "1px solid #E3E9EC" }}>
                      <td style={{ padding: "10px 8px", textAlign: "left" }}>O2Sat (%)</td>
                      {visibleMeasurements.map((m, idx) => (
                        <td key={idx} style={{ padding: "8px 4px", borderLeft: "1px solid #E3E9EC" }}>
                          <span style={severityStyle("SPO2", m.values?.["SPO2"])}>{m.values?.["SPO2"] ?? "NA"}</span>
                        </td>
                      ))}
                      <td style={{ padding: "4px 6px", borderLeft: "1px solid #E3E9EC" }}>
                        <input
                          id="vit_spo2"
                          type="number"
                          placeholder="NA"
                          value={newVitals.spo2}
                          onChange={(e) => handleVitalChange("spo2", e.target.value)}
                          style={{
                              color: severityStyle("SPO2", newVitals.spo2).color,
                            width: "100%",
                            boxSizing: "border-box",
                            padding: "4px 8px",
                            textAlign: "center",
                            border: "1px solid #CBD3D7",
                            borderRadius: 4,
                            fontSize: 14,
                          }}
                        />
                      </td>
                    </tr>

                    {/* Respiratory rate */}
                    <tr style={{ borderTop: "1px solid #E3E9EC" }}>
                      <td style={{ padding: "10px 8px", textAlign: "left" }}>RESPIRATORY RATE</td>
                      {visibleMeasurements.map((m, idx) => (
                        <td key={idx} style={{ padding: "8px 4px", borderLeft: "1px solid #E3E9EC" }}>
                          <span style={severityStyle("RR", m.values?.["RR"])}>{m.values?.["RR"] ?? "NA"}</span>
                        </td>
                      ))}
                      <td style={{ padding: "4px 6px", borderLeft: "1px solid #E3E9EC" }}>
                        <input
                          id="vit_rr"
                          type="number"
                          placeholder="NA"
                          value={newVitals.rr}
                          onChange={(e) => handleVitalChange("rr", e.target.value)}
                          style={{
                              color: severityStyle("RR", newVitals.rr).color,
                            width: "100%",
                            boxSizing: "border-box",
                            padding: "4px 8px",
                            textAlign: "center",
                            border: "1px solid #CBD3D7",
                            borderRadius: 4,
                            fontSize: 14,
                          }}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Bottom Form Controls for Vital Sign */}
            <div
              style={{
                marginTop: 14,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "8px 14px",
                justifyContent: "flex-end",
              }}
            >
              {/* Remark */}
              <span style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
                <label htmlFor="vt_rm" style={{ fontWeight: 600 }}>Remark:</label>
                <select
                  id="vt_rm"
                  className="in"
                  value={newVitals.remark}
                  onChange={(e) => handleVitalChange("remark", e.target.value)}
                  style={{ minHeight: 38, width: 170, borderRadius: 4, border: "1px solid #CBD3D7" }}
                >
                  {VITAL_REMARK_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </span>

              {/* Manual Date/Time */}
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={isManualDateTime}
                  onChange={(e) => setIsManualDateTime(e.target.checked)}
                  style={{ width: 18, height: 18 }}
                />
                วัน/เวลา ที่บันทึก (ระบุเอง)
              </label>

              {isManualDateTime && (
                <>
                  <input
                    id="vt_date"
                    aria-label="วันที่บันทึก"
                    placeholder="วว/ดด/ปปปป"
                    value={newVitals.manualDate}
                    onChange={(e) => handleVitalChange("manualDate", e.target.value)}
                    style={{ width: 130, minHeight: 38, padding: "0 8px", borderRadius: 4, border: "1px solid #CBD3D7" }}
                  />
                  <input
                    id="vt_time"
                    aria-label="เวลาที่บันทึก"
                    placeholder="ชช:นน"
                    value={newVitals.manualTime}
                    onChange={(e) => handleVitalChange("manualTime", e.target.value)}
                    style={{ width: 90, minHeight: 38, padding: "0 8px", borderRadius: 4, border: "1px solid #CBD3D7" }}
                  />
                </>
              )}

              {/* Save Vital Sign Button */}
              <button
                type="button"
                className="bt"
                onClick={handleSaveVitalSign}
                disabled={isSavingVital || !vitalInputComplete}
                aria-disabled={!vitalInputComplete}
                style={{
                  minHeight: 44,
                  padding: "0 24px",
                  borderRadius: 4,
                  border: 0,
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#fff",
                  background: vitalInputComplete ? "#27A844" : "#8C9BA0",
                  cursor: vitalInputComplete ? "pointer" : "not-allowed",
                }}
              >
                {isSavingVital ? "กำลังบันทึก..." : "Save"}
              </button>
            </div>

            {!divisionHasVitalTypes && (
              <div
                role="alert"
                style={{
                  marginTop: 8,
                  padding: "10px 12px",
                  borderRadius: 6,
                  background: "#FFF3DC",
                  color: "#6B4300",
                  fontSize: 13,
                }}
              >
                หน่วยงานของ Encounter นี้ยังไม่กำหนดชนิดสัญญาณชีพ (VitalSignType ของหน่วยงานว่าง)
                จึงบันทึกการวัดไม่ได้ - ต้องกำหนดชนิดวัดที่ master ของหน่วยงานก่อน
              </div>
            )}

            {divisionHasVitalTypes && !vitalInputComplete && (
              <div style={{ marginTop: 6, textAlign: "right", fontSize: 13, color: "#8A3B00" }}>
                กรอก {requiredVitalHintFor(allowedVitalCodes)} ให้ครบเป็นตัวเลข
                {isManualDateTime && " และระบุวัน/เวลาให้ถูกต้อง (วว/ดด/ปปปป และ ชช:นน)"}
              </div>
            )}

            {vitalSavedText && (
              <div style={{ marginTop: 6, textAlign: "right", fontSize: 13, color: "#17592B" }}>
                {vitalSavedText}
              </div>
            )}
          </div>
        )}
      </section>

      {/* ============================================================== */}
      {/* SECTION 4: BODY COMPOSITION                                    */}
      {/* ============================================================== */}
      <section id="sec-bc" className="card" style={{ background: "#fff", padding: "12px 8px 16px", borderRadius: 6, border: "1px solid #D5DEE2" }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "8px 14px",
            paddingBottom: 8,
            borderBottom: "1px solid #DDE3E6",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#1F2A30" }}>
              3.1 · ผลวิเคราะห์องค์ประกอบร่างกาย (Body Composition)
              <SectionSpinner running={bcSectionLoading} label="กำลังโหลด/บันทึกองค์ประกอบร่างกาย" />
            </h2>
          </div>

          <label
            htmlFor="bc_file"
            className="bt"
            style={{
              marginLeft: "auto",
              background: "#1B7FCC",
              color: "#fff",
              cursor: "pointer",
              borderRadius: 4,
              padding: "6px 14px",
              fontSize: 14,
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Paperclip size={16} /> แนบไฟล์ผลตรวจ (PDF / รูปภาพ)
          </label>
          <input
            id="bc_file"
            ref={fileInputRef}
            type="file"
            accept=".pdf,image/*"
            onChange={handleFileUpload}
            style={{ display: "none" }}
          />
        </div>

        {/* ข้อ 1: แสดงชื่อไฟล์และขนาด (KB) ถ้ายังไม่แนบแสดง "ยังไม่ได้แนบไฟล์" */}
        <div style={{ margin: "8px 0", fontSize: 13, color: "#5B6C73" }}>
          {bcFile
            ? `${bcFile.name} · ${Math.max(1, Math.round(bcFile.size / 1024))} KB`
            : "ยังไม่ได้แนบไฟล์ · แนบไฟล์ผลตรวจเพื่ออ่านค่าอัตโนมัติ (OCR) หรือพิมพ์ค่าเองได้"}
        </div>

        {/* ข้อ 2: กำลังอ่านข้อความจากไฟล์ (OCR) จนกว่าจะเสร็จ */}
        {bcOcrLoading && (
          <div
            role="status"
            style={{
              margin: "10px 0",
              padding: "10px 14px",
              borderRadius: 6,
              background: "#FFF3DC",
              color: "#6B4300",
              fontSize: 14,
            }}
          >
            กำลังอ่านข้อความจากไฟล์ (OCR)… · {BODY_COMPOSITION_OCR_WAIT_HINT}
          </div>
        )}

        <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 14, alignItems: "flex-start" }}>
          {/* ขวา (ตามแบบ): chip เลือกค่า + กราฟแนวโน้ม + ◀ ▶ */}
          <div style={{ flex: "1 1 320px", minWidth: 300 }}>
            <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "6px 8px", marginBottom: 8 }}>
              {BODY_COMP_METRICS.map((f) => {
                const isSelected = bcSelectedMetric === f.k;
                return (
                  <button
                    key={f.k}
                    type="button"
                    onClick={() => setBcSelectedMetric(f.k)}
                    aria-pressed={isSelected}
                    style={{
                      minHeight: 30,
                      padding: "0 10px",
                      borderRadius: 15,
                      fontSize: 12,
                      border: `1px solid ${isSelected ? "#1B7FCC" : "#CBD3D7"}`,
                      background: isSelected ? "#1B7FCC" : "#fff",
                      color: isSelected ? "#fff" : "#243036",
                      cursor: "pointer",
                    }}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>

            <div style={{ textAlign: "center", fontSize: 14, fontWeight: 600 }}>
              {bcMetric.label}
              {bcMetric.unit ? ` (${bcMetric.unit})` : ""}
            </div>

            <svg viewBox="0 0 470 260" width="100%" role="img" aria-label={`กราฟแนวโน้ม ${bcMetric.label}`}>
              {bcGridLines.map((line, index) => (
                <g key={`bc-grid-${index}`}>
                  <line x1={bcGraphArea.left} x2={bcGraphArea.right} y1={line.y} y2={line.y} stroke="#E4E4E4" strokeWidth={1} />
                  <text x={38} y={line.ty} textAnchor="end" fontSize={12} fill="#555">
                    {line.t}
                  </text>
                </g>
              ))}
              <line x1={bcGraphArea.left} x2={bcGraphArea.left} y1={bcGraphArea.top} y2={bcGraphArea.bottom} stroke="#999" strokeWidth={1} />
              <line x1={bcGraphArea.left} x2={bcGraphArea.right} y1={bcGraphArea.bottom} y2={bcGraphArea.bottom} stroke="#999" strokeWidth={1} />
              {bcGraphPoints.length > 1 && (
                <polyline
                  points={bcGraphPoints.map((p, i) => `${bcXOf(i)},${bcYOf(p.value)}`).join(" ")}
                  fill="none"
                  stroke="#3FA3BB"
                  strokeWidth={2}
                />
              )}
              {bcGraphPoints.map((p, i) => (
                <g key={`bc-pt-${i}`}>
                  {/* ค่าใหม่ที่ยังไม่บันทึก = จุดสีส้ม (#86619 ข้อ 9) */}
                  <circle cx={bcXOf(i)} cy={bcYOf(p.value)} r={4.5} fill={p.unsaved ? "#E8761B" : "#3FA3BB"} />
                  <text x={bcXOf(i)} y={bcYOf(p.value) - 8} textAnchor="middle" fontSize={12} fontWeight={700} fill="#243036">
                    {bcNumberText(p.value)}
                  </text>
                  <text x={bcXOf(i)} y={232} textAnchor="middle" fontSize={11} fill="#444">
                    {p.date} {p.time}
                  </text>
                </g>
              ))}
            </svg>

            <div style={{ textAlign: "center", fontSize: 12, color: "#5B6C73" }}>
              {bcGraphPoints.length === 0
                ? "ยังไม่มีข้อมูล · แนบไฟล์ผลตรวจเพื่อดูกราฟ"
                : `${bcGraphPoints.length} ครั้งล่าสุด${bcGraphPoints.some((p) => p.unsaved) ? " · จุดสีส้มคือค่าที่ยังไม่บันทึก" : ""}`}
            </div>

            <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 4 }}>
              <button
                type="button"
                onClick={() => setBcPage((prev) => Math.min(bcMaxPage, prev + 1))}
                disabled={bcPage >= bcMaxPage}
                aria-label="ย้อนไปดู Body Composition ครั้งก่อนหน้า"
                style={{
                  minWidth: 46,
                  minHeight: 38,
                  border: 0,
                  borderRadius: 4,
                  background: bcPage >= bcMaxPage ? "#C9D2D6" : "#7B7B7B",
                  color: "#fff",
                  cursor: bcPage >= bcMaxPage ? "not-allowed" : "pointer",
                }}
              >
                ◀
              </button>
              <button
                type="button"
                onClick={() => setBcPage((prev) => Math.max(0, prev - 1))}
                disabled={bcPage <= 0}
                aria-label="ดู Body Composition ครั้งล่าสุด"
                style={{
                  minWidth: 46,
                  minHeight: 38,
                  border: 0,
                  borderRadius: 4,
                  background: bcPage <= 0 ? "#C9D2D6" : "#7B7B7B",
                  color: "#fff",
                  cursor: bcPage <= 0 ? "not-allowed" : "pointer",
                }}
              >
                ▶
              </button>
            </div>
          </div>

          {/* ซ้าย (ตามแบบ): ตารางตัวชี้วัด x ประวัติ 3 ครั้ง + คอลัมน์ "ค่าใหม่" */}
          <div style={{ flex: "1.25 1 380px", minWidth: 0, overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                minWidth: 440,
                borderCollapse: "collapse",
                fontSize: 14,
                textAlign: "center",
                border: "1px solid #E3E9EC",
              }}
            >
              <thead>
                <tr style={{ background: "#EAF4F6", color: "#243036", fontWeight: 600 }}>
                  <th style={{ padding: "10px 8px", textAlign: "left", width: 180 }}>ค่า</th>
                  {bcWindow.map((bc, idx) => (
                    <th key={idx} style={{ padding: "8px 6px", borderLeft: "2px solid #fff", minWidth: 104, lineHeight: 1.25 }}>
                      {bc.dateRecorded}
                      <br />
                      <b>{bc.timeRecorded}</b>
                      {bc.sourceFileUrl && (
                        <div style={{ marginTop: 2 }}>
                          <a
                            href={bc.sourceFileUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={{ fontSize: 11, fontWeight: 400, color: "#0F5F9E" }}
                            aria-label={`เปิดไฟล์ผลตรวจของ ${bc.dateRecorded} ${bc.timeRecorded}`}
                          >
                            📄 ไฟล์ต้นฉบับ
                          </a>
                        </div>
                      )}
                    </th>
                  ))}
                  <th style={{ padding: "10px 8px", borderLeft: "2px solid #fff", background: "#D7EFF4", minWidth: 120, lineHeight: 1.25 }}>
                    ค่าใหม่
                    <br />
                    <span style={{ fontWeight: 400, fontSize: 11 }}>แก้ไขได้</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {BODY_COMP_METRICS.map((f) => {
                  const isHighlight = bcSelectedMetric === f.k;
                  return (
                    <tr
                      key={f.k}
                      style={{
                        borderTop: "1px solid #E3E9EC",
                        background: isHighlight ? "#F1FAFC" : "#fff",
                      }}
                    >
                      <td style={{ padding: "8px 6px", textAlign: "left", fontWeight: isHighlight ? 700 : 400 }}>
                        <button
                          type="button"
                          onClick={() => setBcSelectedMetric(f.k)}
                          aria-label={`เลือกกราฟ ${f.label}`}
                          style={{ border: 0, background: "transparent", textAlign: "left", padding: 0, cursor: "pointer", color: "#243036", fontSize: 14, fontWeight: isHighlight ? 700 : 400 }}
                        >
                          {f.label}
                          <span style={{ fontSize: 11, color: "#6B7C83" }}> {f.unit}</span>
                        </button>
                      </td>
                      {bcWindow.map((bc, idx) => (
                        <td key={idx} style={{ padding: "8px 4px", borderLeft: "1px solid #E3E9EC" }}>
                          {bc[f.k] ?? "NA"}
                        </td>
                      ))}
                      <td style={{ padding: "4px 6px", borderLeft: "1px solid #E3E9EC" }}>
                        <input
                          aria-label={f.label}
                          placeholder="NA"
                          value={newBcRecord[f.k] ?? ""}
                          onChange={(e) =>
                            setNewBcRecord((prev) => ({
                              ...prev,
                              [f.k]: e.target.value ? Number(e.target.value) : undefined,
                            }))
                          }
                          style={{
                            width: "100%",
                            boxSizing: "border-box",
                            padding: "4px 8px",
                            textAlign: "center",
                            border: "1px solid #CBD3D7",
                            borderRadius: 4,
                            fontSize: 14,
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* ข้อ 3: ตัวอย่างไฟล์ที่แนบ + ข้อความ OCR (แก้ไขได้) + วันเวลา ที่ตรวจ */}
        {(bcFile || bcRawText) && (
          <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 14, alignItems: "flex-start" }}>
            <div style={{ flex: "1 1 260px", display: "flex", flexDirection: "column", gap: 6 }}>
              <b style={{ fontSize: 14 }}>ไฟล์ที่แนบ</b>
              {bcFile?.kind === "img" && bcFile.url && (
                <img
                  src={bcFile.url}
                  alt="ภาพผลตรวจ Body Composition"
                  style={{ maxWidth: "100%", maxHeight: 150, objectFit: "contain", border: "1px solid #D5DEE2", borderRadius: 4, background: "#fff" }}
                />
              )}
              {bcFile?.kind === "pdf" && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 10, border: "1px solid #D5DEE2", borderRadius: 4, background: "#F7FAFB" }}>
                  <span style={{ fontSize: 24 }} aria-hidden="true">
                    📄
                  </span>
                  <span style={{ fontSize: 14, overflowWrap: "anywhere" }}>{bcFile.name}</span>
                </div>
              )}
              {bcAmbiguities.length > 0 && (
                <div role="note" style={{ fontSize: 12, color: "#6B4300", background: "#FFF7E6", border: "1px solid #F2D9A6", borderRadius: 4, padding: "6px 10px" }}>
                  ข้อสังเกตจากการอ่านไฟล์:
                  <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                    {bcAmbiguities.map((note, index) => (
                      <li key={index}>{note}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div style={{ flex: "2 1 340px", display: "flex", flexDirection: "column", gap: 6 }}>
              <label htmlFor="bc_raw" style={{ fontSize: 13, fontWeight: 600 }}>
                ข้อความที่อ่านได้จากไฟล์ (OCR) · ตรวจสอบหรือแก้ไข แล้วกด "แปลงค่าใหม่"
              </label>
              <textarea
                id="bc_raw"
                rows={5}
                value={bcRawText}
                onChange={(e) => setBcRawText(e.target.value)}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  borderRadius: 4,
                  border: "1px solid #CBD3D7",
                  padding: "6px 10px",
                  fontSize: 12,
                  lineHeight: 1.5,
                }}
              />
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
                <button
                  type="button"
                  className="bt"
                  onClick={handleReparseBc}
                  disabled={bcOcrLoading}
                  style={{
                    background: "#fff",
                    color: "#243036",
                    border: "1px solid #CBD3D7",
                    borderRadius: 4,
                    padding: "4px 12px",
                    fontSize: 13,
                    cursor: bcOcrLoading ? "not-allowed" : "pointer",
                  }}
                >
                  {bcOcrLoading ? `กำลังแปลงค่า... · ${BODY_COMPOSITION_OCR_WAIT_HINT}` : "แปลงค่าใหม่จากข้อความ"}
                </button>
                <label htmlFor="bc_at" style={{ fontSize: 13 }}>
                  วันที่ เวลา ที่ตรวจ
                </label>
                <input
                  id="bc_at"
                  value={bcDate}
                  placeholder="วว/ดด/ปปปป"
                  onChange={(e) => setBcDate(e.target.value)}
                  style={{ width: 110, padding: "4px 8px", borderRadius: 4, border: "1px solid #CBD3D7", fontSize: 13 }}
                />
                <input
                  aria-label="เวลาที่ตรวจ"
                  value={bcTime}
                  placeholder="ชช:นน"
                  onChange={(e) => setBcTime(e.target.value)}
                  style={{ width: 80, padding: "4px 8px", borderRadius: 4, border: "1px solid #CBD3D7", fontSize: 13 }}
                />
              </div>
            </div>
          </div>
        )}

        {/* ข้อ 5 + 6: ปุ่มล้างไฟล์ และ ปุ่มบันทึก (ปิดเมื่อกาณฑ์ไม่ครบ) */}
        {(bcFile || Object.keys(newBcRecord).length > 0 || bcRawText) && (
          <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "flex-end", alignItems: "center" }}>
            <button
              type="button"
              className="bt"
              onClick={clearBcDraft}
              style={{
                background: "#fff",
                color: "#243036",
                border: "1px solid #CBD3D7",
                borderRadius: 4,
                padding: "6px 16px",
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              ล้างไฟล์
            </button>
            <button
              type="button"
              className="bt"
              onClick={handleSaveBodyComp}
              disabled={bcSaveLoading || bcSaveBlocked}
              aria-disabled={bcSaveBlocked}
              style={{
                background: bcSaveBlocked ? "#8C9BA0" : "#1E8E3E",
                color: "#fff",
                border: 0,
                borderRadius: 4,
                padding: "6px 20px",
                fontSize: 14,
                fontWeight: 600,
                cursor: bcSaveBlocked ? "not-allowed" : "pointer",
              }}
            >
              {bcSaveLoading ? "กำลังบันทึก..." : "บันทึกผลองค์ประกอบร่างกาย"}
            </button>
          </div>
        )}

        {(bcFile || Object.keys(newBcRecord).length > 0 || bcRawText) && bcSaveBlocked && (
          <div role="note" style={{ marginTop: 8, fontSize: 13, color: "#8A3B00" }}>
            {BODY_COMPOSITION_SAVE_HINT}
          </div>
        )}

        {bcSavedMessage && (
          <div style={{ marginTop: 8, padding: "8px 12px", borderRadius: 4, background: "#E3F5E8", color: "#17592B", fontSize: 13 }}>
            {bcSavedMessage}
          </div>
        )}

        {bcMessage && (
          <div style={{ marginTop: 8, padding: "8px 12px", borderRadius: 4, background: "#FFF3DC", color: "#6B4300", fontSize: 13 }}>
            {bcMessage}
          </div>
        )}
      </section>

      {/* ============================================================== */}
      {/* STICKY FOOTER                                                  */}
      {/* ============================================================== */}
      <div
        style={{
          position: "sticky",
          bottom: 0,
          display: "flex",
          flexWrap: "wrap",
          gap: "10px 16px",
          alignItems: "center",
          padding: "14px 16px",
          background: "#fff",
          border: "1px solid #D5DEE2",
          borderRadius: 6,
          boxShadow: "0 -2px 8px rgba(0,0,0,0.06)",
          zIndex: 10,
        }}
      >
        <span
          style={{
            flex: "1 1 280px",
            minWidth: 0,
            fontSize: 14,
            fontWeight: 500,
            color: validation.missingFields.length > 0 ? "#8A3B00" : "#17592B",
          }}
        >
          {validation.missingFields.length > 0
            ? `ยังขาด: ${validation.missingFields.join(", ")}`
            : isSent
            ? "ส่งเข้าคิวรอตรวจแล้ว"
            : "ครบแล้ว พร้อมส่งเข้าคิวรอตรวจ"}
        </span>

        <button
          type="button"
          className="bt"
          onClick={handleSaveTriage}
          disabled={isSavingTriage}
          style={{
            background: "#1E8E3E",
            color: "#fff",
            border: 0,
            borderRadius: 4,
            padding: "8px 20px",
            fontSize: 15,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {isSavingTriage ? "กำลังบันทึก..." : "บันทึกการคัดกรอง"}
        </button>

        <button
          type="button"
          className="bt"
          onClick={handleSendToQueue}
          disabled={isSendingQueue || isSent || !readyForQueue}
          style={{
            background: readyForQueue && !isSent ? "#B6400C" : "#8C9BA0",
            color: "#fff",
            border: 0,
            borderRadius: 4,
            padding: "8px 22px",
            fontSize: 15,
            fontWeight: 700,
            cursor: readyForQueue && !isSent ? "pointer" : "not-allowed",
          }}
        >
          {isSendingQueue ? "กำลังส่ง..." : isSent ? "ส่งเข้าคิวแล้ว" : "ส่งผู้ป่วยเข้าคิวรอตรวจ"}
        </button>
      </div>
    </div>
  );
};

export default CardScreenV4;
